import { useEffect, useRef, useState } from 'react';
import { getFeaturedReviews, type FeaturedReview } from '../../lib/site-api';

// Reviews shown on the landing pages. Which reviews appear is decided
// by admins, who "star" individual reviews in the admin Reviews screen
// (GET /public/reviews/featured returns those). The section renders
// nothing until at least one review is featured, so the page never
// shows an empty band.

const composeName = (
  first: string | null | undefined,
  last: string | null | undefined,
): string => {
  const full = [first, last].filter(Boolean).join(' ').trim();
  return full || 'A Cherry Picked customer';
};

const initialsOf = (
  first: string | null | undefined,
  last: string | null | undefined,
): string => {
  const a = (first ?? '').trim();
  const b = (last ?? '').trim();
  const letters = `${a.charAt(0)}${b.charAt(0)}`.toUpperCase();
  return letters || 'CP';
};

export function FeaturedReviews() {
  const [reviews, setReviews] = useState<FeaturedReview[]>([]);
  const [loaded, setLoaded] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    getFeaturedReviews()
      .then((res) => {
        if (!cancelled) setReviews(res.data ?? []);
      })
      .catch(() => {
        // Fail silently — the section simply hides if the call fails.
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Nothing to show (still loading on first paint, or no featured
  // reviews) → render nothing rather than an empty band.
  if (!loaded || reviews.length === 0) return null;

  // Scroll the carousel by roughly one card (card width + the gap).
  const scrollByCard = (dir: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector<HTMLElement>('[data-review-card]');
    const amount = card
      ? card.offsetWidth + 24
      : Math.round(track.clientWidth * 0.8);
    track.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  return (
    <section className="py-16 bg-[#f2eee2] pb-3">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header + carousel arrows */}
        <div className="flex items-center justify-between mb-8 gap-4">
          <h2 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[26px] md:text-[34px] leading-tight">
            What our customers say
          </h2>
          {reviews.length > 1 ? (
            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={() => scrollByCard(-1)}
                aria-label="Previous reviews"
                className="w-9 h-9 rounded-full border border-[#8e2a2f]/40 flex items-center justify-center text-[#8e2a2f] hover:bg-[#8e2a2f] hover:text-white transition-colors"
              >
                <span className="material-icons text-[20px]">chevron_left</span>
              </button>
              <button
                type="button"
                onClick={() => scrollByCard(1)}
                aria-label="Next reviews"
                className="w-9 h-9 rounded-full border border-[#8e2a2f]/40 flex items-center justify-center text-[#8e2a2f] hover:bg-[#8e2a2f] hover:text-white transition-colors"
              >
                <span className="material-icons text-[20px]">chevron_right</span>
              </button>
            </div>
          ) : null}
        </div>

        {/* Carousel track — horizontal scroll-snap. 1 card on mobile,
            2 on tablet, 3 on desktop; the arrows scroll by one card.
            Scrollbar hidden; still swipeable on touch. */}
        <div
          ref={trackRef}
          className="flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {reviews.map((review) => {
            const name = composeName(
              review.customer?.first_name,
              review.customer?.last_name,
            );
            return (
              <div
                key={review.id}
                data-review-card
                className="snap-start shrink-0 basis-full sm:basis-[calc(50%-12px)] lg:basis-[calc(33.333%-16px)] bg-white border border-[#eee] rounded-2xl overflow-hidden flex flex-col"
              >
                <div className="p-6 flex-1">
                  <p
                    className="font-['DM_Sans',sans-serif] text-[#3a3335] text-sm leading-relaxed"
                    style={{ fontVariationSettings: "'opsz' 14" }}
                  >
                    “{review.comment}”
                  </p>
                </div>
                <div className="bg-[#8e2a2f] px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-md bg-white/15 flex items-center justify-center font-['DM_Sans',sans-serif] font-bold text-white text-xs uppercase">
                      {initialsOf(
                        review.customer?.first_name,
                        review.customer?.last_name,
                      )}
                    </div>
                    <div>
                      <p
                        className="font-['DM_Sans',sans-serif] text-[#eee] text-sm"
                        style={{ fontVariationSettings: "'opsz' 14" }}
                      >
                        {name}
                      </p>
                      {review.service?.name ? (
                        <p
                          className="font-['DM_Sans',sans-serif] text-white text-xs"
                          style={{ fontVariationSettings: "'opsz' 14" }}
                        >
                          {review.service.name}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[#e8cd96] text-lg">★</span>
                    <span
                      className="font-['DM_Sans',sans-serif] text-[#eee] text-sm"
                      style={{ fontVariationSettings: "'opsz' 14" }}
                    >
                      {Number(review.rating).toFixed(1)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
